/**
 * Core API client for standardized API interactions
 *
 * This service provides a consistent approach to API calls, response handling,
 * error management, and data formatting for The Portal.
 */

// Import any required libraries
import { useAuthStore } from '../stores/auth';
import { Notify } from 'quasar';

/**
 * Base API URL - defaults to relative path for production
 * In development, this would be the full URL of the API server
 */
const API_BASE_URL = '/api';

/**
 * Core API client with standardized methods for API interactions
 */
export const api = {
  /**
   * Get the authorization headers including the JWT token
   * @param {boolean} isFormData - Whether this is a FormData request (skips Content-Type)
   * @returns {Object} Headers object with Authorization if token exists
   */
  getHeaders(isFormData = false) {
    // Get auth store to access the token
    const authStore = useAuthStore();
    const headers = {
      'Accept': 'application/json',
    };

    // Only set Content-Type for non-FormData requests
    // FormData requests need browser to set Content-Type with boundary
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    // Add CSRF token for Laravel
    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    if (csrfToken) {
      headers['X-CSRF-TOKEN'] = csrfToken.getAttribute('content');
    }

    // Add auth token if available (will use Laravel Sanctum)
    if (authStore.token) {
      headers['Authorization'] = `Bearer ${authStore.token}`;
    }

    return headers;
  },

  /**
   * Format query parameters for URL
   * @param {Object} params - Parameters to format
   * @returns {string} Formatted query string or empty string
   */
  formatQueryParams(params) {
    if (!params || Object.keys(params).length === 0) {
      return '';
    }

    // Handle special cases for standardized parameters
    const queryParams = new URLSearchParams();
    
    // Handle pagination parameters
    if (params.page) {queryParams.append('page', params.page);}
    if (params.limit) {queryParams.append('limit', params.limit);}
    if (params.perPage) {queryParams.append('limit', params.perPage);}
    
    // Handle include parameters (convert array to comma-separated)
    if (params.include) {
      if (Array.isArray(params.include)) {
        queryParams.append('include', params.include.join(','));
      } else {
        queryParams.append('include', params.include);
      }
    }
    
    // Handle filter parameter (convert object to JSON string)
    if (params.filter) {
      if (typeof params.filter === 'object') {
        queryParams.append('filter', JSON.stringify(params.filter));
      } else {
        queryParams.append('filter', params.filter);
      }
    }
    
    // Handle search parameter (only map q/query aliases, pass 'search' as-is)
    if (params.q || params.query) {
      queryParams.append('q', params.q || params.query);
    }
    
    // Handle sort parameters
    if (params.sort) {
      queryParams.append('sort', params.sort);
      if (params.order) {queryParams.append('order', params.order);}
    }
    
    // Add any remaining parameters
    Object.entries(params).forEach(([key, value]) => {
      // Skip parameters already handled and special parameters that shouldn't be sent to backend
      if (['page', 'limit', 'perPage', 'include', 'filter', 'q', 'query', 'sort', 'order'].includes(key)) {
        return;
      }
      
      // Skip cache buster and other frontend-only params
      if (key === '_' || key === 't' || key === 'timestamp' || key === 'cache' || 
          key === 'nocache' || key === 'cb' || key.startsWith('_') || 
          key === 'cacheBuster' || key === 'timestamp') {
        return;
      }
      queryParams.append(key, value);
    });
    
    const queryString = queryParams.toString();
    return queryString ? `?${queryString}` : '';
  },

  /**
   * Process the API response according to standardized format
   * @param {Response} response - Fetch Response object
   * @returns {Promise<Object>} Processed response data
   * @throws {Error} Standardized error with message and status
   */
  async handleResponse(response) {
    const authStore = useAuthStore();

    // Handle 401 Unauthorized - redirect to Laravel login page
    if (response.status === 401) {
      // Clear any stored auth data
      authStore.clearAuth();
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');

      // Show notification
      Notify.create({
        type: 'negative',
        message: 'Your session has expired. Please log in again.',
        timeout: 3000,
      });

      // Redirect to Laravel blade login page with current path as redirect
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = '/login' + (currentPath && currentPath !== '/' ? '?redirect=' + encodeURIComponent(currentPath) : '');
      // Throw error to stop further processing
      throw new Error('Unauthorized - redirecting to login');
    }
    
    // Handle 403 Forbidden - User doesn't have permission
    if (response.status === 403) {

      // Get the response data to check for specific error message
      let data;
      try {
        data = await response.json();
      } catch (error) {
        console.warn('Failed to parse 403 response body:', error);
        data = { message: 'You do not have permission to access this resource' };
      }

      // Show user-friendly notification
      Notify.create({
        type: 'negative',
        message: data.message || 'Access denied. You do not have permission for this action.',
        timeout: 5000,
        actions: [
          { label: 'Dismiss', color: 'white', handler: () => {} },
        ],
      });

      // Create error for handling in calling code
      const error = new Error(data.message || 'Access denied');
      error.status = 403;
      error.data = data;
      error.url = response.url;
      throw error;
    }
    
    // Handle 429 Too Many Requests - Rate limiting
    if (response.status === 429) {

      // Get retry-after header
      const retryAfter = response.headers.get('Retry-After');
      const retrySeconds = retryAfter ? parseInt(retryAfter) : 60;

      // Get the response data
      let data;
      try {
        data = await response.json();
      } catch (error) {
        console.warn('Failed to parse 429 response body:', error);
        data = { message: `Too many requests. Please wait ${retrySeconds} seconds.` };
      }

      // Show notification with retry information
      Notify.create({
        type: 'warning',
        message: data.message || `Too many requests. Please wait ${retrySeconds} seconds before trying again.`,
        timeout: retrySeconds * 1000, // Show for the duration of the rate limit
        progress: true,
        actions: [
          { label: 'OK', color: 'white', handler: () => {} },
        ],
      });

      // Create error with retry information
      const error = new Error(data.message || 'Too many requests');
      error.status = 429;
      error.retryAfter = retrySeconds;
      error.data = data;
      error.url = response.url;
      throw error;
    }
    
    // Handle 422 Unprocessable Entity - Validation errors
    if (response.status === 422) {
      let data;
      try {
        data = await response.json();
      } catch (error) {
        console.warn('Failed to parse 422 response body:', error);
        data = { message: 'Validation failed' };
      }

      // Show validation errors
      if (data.errors) {
        Object.entries(data.errors).forEach(([field, messages]) => {
          const message = Array.isArray(messages) ? messages[0] : messages;
          Notify.create({
            type: 'negative',
            message: `${field}: ${message}`,
            timeout: 5000,
          });
        });
      } else {
        Notify.create({
          type: 'negative',
          message: data.message || 'Validation failed. Please check your input.',
          timeout: 5000,
        });
      }

      const error = new Error(data.message || 'Validation failed');
      error.status = 422;
      error.data = data;
      error.errors = data.errors;
      error.url = response.url;
      throw error;
    }
    
    // Handle 500 Internal Server Error
    if (response.status === 500) {

      Notify.create({
        type: 'negative',
        message: 'Server error occurred. Please try again later or contact support.',
        timeout: 5000,
        actions: [
          { label: 'Dismiss', color: 'white', handler: () => {} },
        ],
      });

      const error = new Error('Server error occurred');
      error.status = 500;
      error.url = response.url;
      throw error;
    }
    
    // Get the response data with better error handling
    let data;
    try {
      data = await response.json();
    } catch (error) {
      // Handle non-JSON responses (like HTML error pages)
      if (response.ok) {
        // If successful but no JSON, return empty object
        return {};
      }
      const parseError = new Error(`Failed to parse response: ${error.message}`);
      parseError.status = response.status;
      parseError.response = response;
      throw parseError;
    }
    
    // Check if response is successful
    if (!response.ok) {
      // Create standardized error object with more detail for debugging
      
      // Show generic error notification for other status codes
      if (response.status !== 403 && response.status !== 429 && response.status !== 422) {
        Notify.create({
          type: 'negative',
          message: data.message || data.error || `Error: ${response.statusText}`,
          timeout: 5000,
        });
      }
      
      const error = new Error(data.message || data.error || 'Unknown API error');
      error.status = response.status;
      error.data = data;
      error.url = response.url;
      throw error;
    }
    
    // Handle standardized success response format { success: true, data: {...} }
    if (data.success === true && data.data !== undefined) {
      // Preserve pagination metadata when present (for paginated endpoints)
      if (data.pagination) {
        return { data: data.data, pagination: data.pagination };
      }
      return data.data;
    }
    
    // Handle legacy response formats that don't follow the standard structure
    // but still represent successful responses
    if (response.ok) {
      // If response has status:success but not wrapped in data property
      if (data.status === 'success' && data.data) {
        return data.data;
      }
      
      // If simple array or object response without wrapper
      return data;
    }
    
    // Fallback for unknown response format
    return data;
  },

  /**
   * Standardized error handler
   * @param {Error} error - Error object
   * @returns {Object} Standardized error object
   */
  handleError(error) {
    
    return {
      message: error.message || 'Unknown error occurred',
      status: error.status || 500,
      data: error.data || null,
    };
  },

  /**
   * Make a standardized API request with retry logic for rate limiting
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @param {number} retryCount - Current retry attempt (used internally)
   * @returns {Promise<Object>} Processed response data
   */
  async request(endpoint, options = {}, retryCount = 0) {
    try {
      // Ensure endpoint starts with / for path join
      const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      const url = `${API_BASE_URL}${path}`;

      // Check if data is FormData
      const isFormData = options.data instanceof FormData;

      // Set up fetch options with authentication
      const fetchOptions = {
        method: options.method || 'GET',
        headers: this.getHeaders(isFormData),
        credentials: 'include', // Include cookies for CORS
        ...options,
      };

      // Add body for POST/PUT requests
      if (options.data && ['POST', 'PUT', 'PATCH'].includes(fetchOptions.method)) {
        // For FormData, pass it directly - browser will handle serialization
        // For other data, convert to JSON
        fetchOptions.body = isFormData ? options.data : JSON.stringify(options.data);
      }

      // Make the request
      const response = await fetch(url, fetchOptions);

      // Process the response
      return await this.handleResponse(response);
    } catch (error) {
      // Handle rate limiting with automatic retry
      if (error.status === 429 && retryCount < 1 && options.autoRetry !== false) {
        const retryAfter = error.retryAfter || 60;

        // Show notification about automatic retry
        Notify.create({
          type: 'info',
          message: `Request will be retried automatically in ${retryAfter} seconds...`,
          timeout: retryAfter * 1000,
          progress: true,
        });

        // Wait for the specified time
        await new Promise((resolve) => setTimeout(resolve, retryAfter * 1000));

        // Retry the request
        return this.request(endpoint, options, retryCount + 1);
      }
      
      // Handle any other errors
      throw this.handleError(error);
    }
  },

  /**
   * Make a GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Processed response data
   */
  async get(endpoint, params = {}) {
    // Create a clean copy of params without cache busters
    const cleanParams = { ...params };
    
    // Remove any existing cache busters that might have been included
    const cacheBusterKeys = ['_', 't', 'timestamp', 'cache', 'nocache'];
    cacheBusterKeys.forEach((key) => delete cleanParams[key]);
    
    // Generate the query string without cache busters
    const queryString = this.formatQueryParams(cleanParams);
    
    // Generate unique cache buster
    const uniqueValue = Date.now() + Math.random().toString(36).substring(2, 15);
    
    // Add cache buster as a separate URL parameter, not as a filter/query param
    const cacheBuster = `cb=${uniqueValue}`; // Use 'cb' instead of '_' to be more explicit
    const separator = queryString ? '&' : '?';
    const fullUrl = `${endpoint}${queryString}${separator}${cacheBuster}`;
    
    return this.request(fullUrl, { method: 'GET' });
  },

  /**
   * Make a POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<Object>} Processed response data
   */
  async post(endpoint, data = {}) {
    return this.request(endpoint, { method: 'POST', data });
  },

  /**
   * Make a PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<Object>} Processed response data
   */
  async put(endpoint, data = {}) {
    return this.request(endpoint, { method: 'PUT', data });
  },

  /**
   * Make a PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<Object>} Processed response data
   */
  async patch(endpoint, data = {}) {
    return this.request(endpoint, { method: 'PATCH', data });
  },

  /**
   * Make a DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters (e.g., {cascade: true})
   * @returns {Promise<Object>} Processed response data
   */
  async delete(endpoint, params = {}) {
    const queryString = this.formatQueryParams(params);
    const fullUrl = `${endpoint}${queryString}`;
    return this.request(fullUrl, { method: 'DELETE' });
  },

  /**
   * Get paginated data with standardized format
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Pagination parameters (page, limit)
   * @returns {Promise<Object>} Paginated response with data and pagination metadata
   */
  async getPaginated(endpoint, params = {}) {
    // Ensure pagination params are present, but don't include cache busters
    const filteredParams = { ...params };
    // Remove any cache busters or timestamps
    delete filteredParams._;
    delete filteredParams.timestamp;
    delete filteredParams.cache;
    delete filteredParams.nocache;

    const paginationParams = {
      page: filteredParams.page || 1,
      limit: filteredParams.limit || filteredParams.perPage || 25,
      ...filteredParams,
    };

    // Make paginated request
    const response = await this.get(endpoint, paginationParams);

    // Handle different pagination formats
    if (response.pagination) {
      // Already follows standard format { data: [...], pagination: {...} }
      return response;
    }

    // Handle Laravel Resource Collection format with meta object
    // Laravel Resource Collections return: { data: [...], meta: { current_page, per_page, total, last_page, ... } }
    if (response.data && Array.isArray(response.data) && response.meta) {

      const pagination = {
        current_page: response.meta.current_page || paginationParams.page,
        per_page: response.meta.per_page || paginationParams.limit,
        total_items: response.meta.total || response.data.length,
        total_pages: response.meta.last_page || 1,
      };

      return {
        data: response.data,
        pagination,
      };
    }

    // Handle Laravel paginated resource format (legacy - pagination at top level)
    // Laravel returns: { data: [...items], current_page, per_page, total, last_page, ... }
    if (response.data && Array.isArray(response.data) && (response.current_page || response.per_page || response.total)) {
      // This is Laravel's paginated resource format

      const pagination = {
        current_page: response.current_page || paginationParams.page,
        per_page: response.per_page || paginationParams.limit,
        total_items: response.total || response.data.length,
        total_pages: response.last_page || 1,
      };

      return {
        data: response.data,
        pagination,
      };
    }

    // Handle legacy or non-standard pagination formats
    if (Array.isArray(response)) {
      // Simple array response without pagination metadata
      return {
        data: response,
        pagination: {
          current_page: paginationParams.page,
          per_page: paginationParams.limit,
          total_items: response.length,
          total_pages: 1,
        },
      };
    }

    // Generic data array with pagination metadata in other properties
    if (response.data && Array.isArray(response.data)) {
      // Try to extract pagination metadata from other properties
      const pagination = {
        current_page: response.current_page || paginationParams.page,
        per_page: response.per_page || paginationParams.limit,
        total_items: response.total || response.total_items || response.data.length,
        total_pages: response.total_pages || response.last_page || 1,
      };

      return {
        data: response.data,
        pagination,
      };
    }

    // Fallback format
    return {
      data: Array.isArray(response) ? response : [response],
      pagination: {
        current_page: paginationParams.page,
        per_page: paginationParams.limit,
        total_items: Array.isArray(response) ? response.length : 1,
        total_pages: 1,
      },
    };
  },
  
  /**
   * Parse and format a date from API
   * @param {string} dateString - ISO date string
   * @returns {Date|null} Date object or null if invalid
   */
  parseDate(dateString) {
    if (!dateString) {return null;}
    
    try {
      // Try direct ISO parsing
      const date = new Date(dateString);
      if (!isNaN(date)) {return date;}
      
      // Try MySQL format
      if (dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
        const isoString = dateString.replace(' ', 'T') + 'Z';
        const date = new Date(isoString);
        if (!isNaN(date)) {return date;}
      }
      
      return null;
    } catch (error) {
      console.warn('Failed to parse date string:', dateString, error);
      return null;
    }
  },

  /**
   * Format a date for display
   * @param {Date|string} date - Date to format
   * @param {string} format - Optional format string ('short', 'long', 'time')
   * @returns {string} Formatted date string
   */
  formatDate(date, format = 'short') {
    if (!date) {return '';}
    
    const dateObj = typeof date === 'string' ? this.parseDate(date) : date;
    if (!dateObj) {return '';}
    
    try {
      switch (format) {
        case 'short':
          return dateObj.toLocaleDateString();
        case 'long':
          return dateObj.toLocaleDateString(undefined, { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric', 
          });
        case 'time':
          return dateObj.toLocaleTimeString();
        case 'datetime':
          return `${dateObj.toLocaleDateString()} ${dateObj.toLocaleTimeString()}`;
        case 'iso':
          return dateObj.toISOString();
        default:
          return dateObj.toLocaleDateString();
      }
    } catch (error) {
      console.warn('Failed to format date:', date, error);
      return '';
    }
  },

  /**
   * Safely parse boolean value
   * @param {any} value - Value to parse
   * @returns {boolean} Parsed boolean
   */
  parseBoolean(value) {
    if (typeof value === 'boolean') {return value;}
    if (typeof value === 'number') {return value !== 0;}
    if (typeof value === 'string') {
      return ['true', '1', 'yes', 'y'].includes(value.toLowerCase());
    }
    return Boolean(value);
  },
};

export default api;